import { History } from 'history';
import { GraphQLError } from 'graphql';
import { LocationErrorState } from 'Components/utils/ApiErrorFallback';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { ApolloLink, Observable } from '@apollo/client';
import { updatePageStatus } from './utils';

const ACCESS_DENIED_MESSAGE = 'access denied';

const isNestedAccessDeniedError = (error: Readonly<GraphQLError>) =>
  // Check if the error path length is > 1 to ensure that
  // the error was generated from a nested field and not from the root query
  error.path?.length > 1 && error.message === ACCESS_DENIED_MESSAGE;

/**
 * A link to react to GraphQL and/or network errors
 */
const createErrorLink = (history: History<LocationErrorState>) => {
  return onError(({ graphQLErrors, networkError, operation, response }: ErrorResponse) => {
    // If the error is not considered a fail, then don't log it to sentry
    if (operation.getContext()?.failSilently) {
      return undefined;
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(error => {
        const statusCode = error.extensions?.statusCode;
        if (statusCode === 404 && operation.getContext()?.ignore404) {
          updatePageStatus(history, 200);
        } else if (statusCode) {
          updatePageStatus(history, statusCode as number);
        }
      });
    }

    let swallow: Observable<never> | undefined;
    if (networkError) {
      let statusCode: LocationErrorState['statusCode'] =
        'statusCode' in networkError ? networkError?.statusCode : undefined;
      // Although most operations return a status code that we can use, that's not always the case. When the API itself
      // is non responsive, we get no status code back, additionally, if the request was blocked by the browser, we also
      // get no status code back but we do get a message, that varies from browser to browser.
      // e.g. Chrome: "Failed to fetch", Firefox: "NetworkError when attempting to fetch resource." etc
      if (!statusCode && !!networkError.message) {
        statusCode = 'unknown';
      }

      // Historically, we logged an error at this point when we encountered a server error (HTTP status >500)
      // This has proven noisy and transient. Instead, we generate metrics from the Realtime User Monitoring (RUM) data.
      // Look for `frontend_errors` in DDog metrics.
      if (statusCode) {
        updatePageStatus(history, statusCode);
      }

      // 401 means the session is gone — ApiErrorFallback already triggers signOut
      // off the page-status change above. Swallow here so callers (especially
      // imperative client.query users) don't surface an unhandled ApolloError.
      if (statusCode === 401) {
        swallow = Observable.of();
      }
    }

    // If the response contains partial data and errors omit nested permission errors
    if (response?.data && response?.errors) {
      response.errors = response.errors.filter(e => !isNestedAccessDeniedError(e));
    }

    return swallow;
  }) as unknown as ApolloLink;
};

export default createErrorLink;
